import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { observeServerTime } from './serverClock';

export type ErrorCode =
  | 'validation_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'not_configured'
  | 'internal_error'
  | 'offline';

export interface FieldIssue {
  path: string;
  message: string;
}

/** Every failure the UI sees: the API's own `{ error }` body, or offline. */
export class ApiError extends Error {
  /**
   * Set by the global handler (src/lib/queryClient.ts) once it has told the
   * person — a toast for offline, rate limits and server trouble, or a trip to
   * sign in. Screens skip a handled error so nothing is said twice.
   */
  handled = false;

  constructor(
    public readonly status: number,
    public readonly code: ErrorCode,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isOffline(): boolean {
    return this.status === 0;
  }

  /** Per-field messages from a validation error, keyed by field path. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details as FieldIssue[]) {
        if (d && typeof d.path === 'string' && !(d.path in out)) out[d.path] = d.message;
      }
    }
    return out;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

/**
 * A failure that goes away by itself: no connection, or the server restarting
 * (nginx answers 502 for the few seconds a deploy restarts the API) or briefly
 * overloaded. Worth trying again; anything else is an answer, not an outage.
 */
export const isTransient = (e: unknown): boolean =>
  isApiError(e) && (e.status === 0 || e.status >= 500);

export const OFFLINE_MESSAGE = 'Could not reach the server. Check your connection and try again.';
const UNKNOWN_MESSAGE = 'Something went wrong. Try again.';

/**
 * Anything thrown, as an ApiError: API failures pass through, a browser or
 * code error keeps its sentence under `internal_error`.
 */
export function toApiError(e: unknown): ApiError {
  if (isApiError(e)) return e;
  const message = e instanceof Error && e.message ? e.message : UNKNOWN_MESSAGE;
  return new ApiError(-1, 'internal_error', message);
}

/** The sentence to show for any error, unless the screen knows a better one. */
export const errorMessage = (e: unknown): string => toApiError(e).message;

/**
 * Failures the app handles in one place, whatever screen made the call: the
 * answer has nothing to do with what the person typed or picked.
 * `unauthenticated` is handled globally only while signed in (session ended);
 * on the sign-in screens it means a wrong password and stays local.
 */
export const GLOBAL_CODES: ReadonlySet<ErrorCode> = new Set([
  'offline',
  'unauthenticated',
  'rate_limited',
  'internal_error',
]);

export interface FormError {
  /** Per-field messages, keyed by field path, for `<Field error>`. */
  fields: Record<string, string>;
  /** One sentence for an inline `Notice` when no field claims the error. */
  message: string | null;
}

/**
 * What a form shows for its mutation's error:
 *
 *   const { fields, message } = formError(save.error);
 *
 * An error the global handler already toasted comes back empty.
 */
export function formError(error: unknown): FormError {
  if (!error) return { fields: {}, message: null };
  const e = toApiError(error);
  if (e.handled) return { fields: {}, message: null };
  const fields = e.fieldErrors();
  return { fields, message: Object.keys(fields).length > 0 ? null : e.message };
}

/**
 * One sentence for a form or dialog with no per-field slots: the error's own
 * sentence, or its first field message. Null for a handled error.
 */
export function inlineError(error: unknown): string | null {
  const { fields, message } = formError(error);
  return message ?? Object.values(fields)[0] ?? null;
}

const clock: Middleware = {
  onRequest({ request }) {
    request.headers.set('X-Client-Time', new Date().toISOString());
    startedAt.set(request, Date.now());
    return request;
  },
  onResponse({ request, response }) {
    observeServerTime(response.headers.get('x-server-time'), startedAt.get(request) ?? Date.now());
    startedAt.delete(request);
    return response;
  },
};
const startedAt = new WeakMap<Request, number>();

/** The typed client. Paths come from the API's OpenAPI document (`pnpm api:types`). */
export const api = createClient<paths>({ baseUrl: '/', credentials: 'include' });
api.use(clock);

type Result<T> = { data?: T; error?: unknown; response: Response };

/**
 * Turns an openapi-fetch result into data or a thrown ApiError. Every query and
 * mutation goes through this so error handling is uniform.
 */
export async function unwrap<T>(call: Promise<Result<T>>): Promise<T> {
  let result: Result<T>;
  try {
    result = await call;
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  const { data, error, response } = result;
  if (response.ok && data !== undefined) return data;
  const body = error as
    { error?: { code?: ErrorCode; message?: string; details?: unknown } } | undefined;
  const code = body?.error?.code ?? codeForStatus(response.status);
  const message = body?.error?.message ?? messageForStatus(response.status);
  throw new ApiError(response.status, code, message, body?.error?.details);
}

function codeForStatus(status: number): ErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status === 400) return 'validation_error';
  return 'internal_error';
}

function messageForStatus(status: number): string {
  if (status === 401) return 'Sign in to continue.';
  if (status === 403) return 'That is not yours to see or change.';
  if (status === 404) return 'That could not be found.';
  if (status === 429) return 'Too many tries. Wait a minute and try again.';
  return 'Something went wrong on our side. Try again in a moment.';
}
